import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import './CertificateDetailPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CertificateDetailPage = () => {
  const { certificate_id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    fetchCertificateDetail();
  }, [certificate_id]);

  const fetchCertificateDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v2/certificates/${certificate_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch certificate details');
      }

      const data = await response.json();
      setCertificate(data);

      // Generate QR Code for public verification link
      if (data.public_token) {
        const verifyUrl = `${window.location.origin}/verify/${data.public_token}`;
        const qrDataUrl = await QRCode.toDataURL(verifyUrl, {
          width: 300,
          margin: 2,
          color: {
            dark: '#333333',
            light: '#FFFFFF'
          }
        });
        setQrCodeUrl(qrDataUrl);
      }

    } catch (error) {
      console.error('Error fetching certificate details:', error);
      alert(t('certificates.detail.fetchError'));
      navigate('/certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v2/certificates/${certificate_id}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate_${certificate.serial_no}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(t('certificates.detail.downloadError'));
    }
  };

  const copyVerifyLink = () => {
    if (certificate && certificate.public_token) {
      const verifyUrl = `${window.location.origin}/verify/${certificate.public_token}`;
      navigator.clipboard.writeText(verifyUrl);
      alert(t('certificates.detail.linkCopied'));
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      1: 'var(--duo-blue)',
      2: 'var(--duo-green)',
      3: 'var(--duo-yellow)',
      4: '#FF9500',
      5: 'var(--duo-purple)',
      6: 'var(--duo-red)'
    };
    return colors[level] || 'var(--duo-blue)';
  };

  const getAPGrade = (ap) => {
    if (ap >= 80) return { label: '최우수 (Distinguished)', color: '#4444ff', emoji: '🏆' };
    if (ap >= 60) return { label: '우수 (Advanced)', color: '#44dd44', emoji: '⭐' };
    if (ap >= 40) return { label: '보통 (Proficient)', color: '#ffdd44', emoji: '✨' };
    if (ap >= 20) return { label: '기초 (Basic)', color: '#ff9944', emoji: '📚' };
    return { label: '기초 이하 (Below Basic)', color: '#ff4444', emoji: '📖' };
  };

  if (loading) {
    return (
      <div className="certificate-detail-page">
        <div className="loading-center">
          <div className="loading-spinner"></div>
          <p>{t('certificates.detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="certificate-detail-page">
        <div className="empty-state">
          <h3>{t('certificates.detail.notFound')}</h3>
          <button className="btn btn-primary" onClick={() => navigate('/certificates')}>
            {t('certificates.detail.backToList')}
          </button>
        </div>
      </div>
    );
  }

  const apGrade = getAPGrade(certificate.absolute_percentile);
  const levelColor = getLevelColor(certificate.level);

  return (
    <div className="certificate-detail-page">
      <div className="certificate-detail-container">

        {/* Back Button */}
        <button className="back-button" onClick={() => navigate('/certificates')}>
          ← {t('certificates.detail.back')}
        </button>

        {/* Certificate Header */}
        <div className="cert-detail-header" style={{ borderTopColor: levelColor }}>
          <div className="cert-header-top">
            <div className="cert-type-badge-large" style={{ background: certificate.scope === 'BOOK' ? 'var(--duo-blue)' : 'var(--duo-purple)' }}>
              {certificate.scope === 'BOOK' ? 'BOOK CERTIFICATE' : 'AGGREGATE CERTIFICATE'}
            </div>
            <div className="cert-status-large" style={{ color: certificate.status === 'active' ? 'var(--duo-green)' : '#999' }}>
              {certificate.status === 'active' ? 'Active' : 'Inactive'}
            </div>
          </div>
          
          <h1 className="cert-detail-title">
            {certificate.scope === 'BOOK' 
              ? certificate.book_title || t('certificates.detail.unknownBook')
              : `${t('certificates.detail.comprehensive')} (${certificate.used_book_count || 0}${t('certificates.detail.books')})`
            }
          </h1>

          <div className="cert-serial-large">
            {t('certificates.detail.serialNo')}: <span>{certificate.serial_no}</span>
          </div>

          <div className="cert-issue-date">
            {t('certificates.detail.issuedOn')}: {new Date(certificate.issued_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {/* Scores Grid */}
        <div className="cert-scores-grid">
          <div className="score-card level-card">
            <div className="score-card-icon" style={{ color: levelColor }}>⭐</div>
            <div className="score-card-label">{t('certificates.detail.level')}</div>
            <div className="score-card-value" style={{ color: levelColor }}>Level {certificate.level}</div>
          </div>

          <div className="score-card overall-card">
            <div className="score-card-icon">📊</div>
            <div className="score-card-label">{t('certificates.detail.overallScore')}</div>
            <div className="score-card-value">{certificate.overall_score}</div>
          </div>

          <div className="score-card ap-card">
            <div className="score-card-icon">{apGrade.emoji}</div>
            <div className="score-card-label">AP</div>
            <div className="score-card-value" style={{ color: apGrade.color }}>{certificate.absolute_percentile}</div>
            <div className="score-card-grade" style={{ color: apGrade.color }}>{apGrade.label}</div>
          </div>
        </div>

        {/* Showcase Responses */}
        {certificate.showcase_responses && certificate.showcase_responses.length > 0 && (
          <div className="showcase-section">
            <h2 className="section-title">
              <span className="section-icon">✨</span>
              {t('certificates.detail.showcaseTitle')}
            </h2>
            <p className="section-subtitle">{t('certificates.detail.showcaseSubtitle')}</p>

            <div className="showcase-grid">
              {certificate.showcase_responses.map((item, index) => (
                <div key={index} className="showcase-card">
                  <div className="showcase-header">
                    <span className="showcase-number">#{index + 1}</span>
                    <span className="showcase-level" style={{ background: getLevelColor(item.level || 1) }}>
                      Level {item.level || 1}
                    </span>
                  </div>
                  <div className="showcase-question">
                    <strong>Q:</strong> {item.question_text || t('certificates.detail.questionNotAvailable')}
                  </div>
                  <div className="showcase-answer">
                    <strong>A:</strong> {item.student_answer || t('certificates.detail.answerNotAvailable')}
                  </div>
                  {item.score && (
                    <div className="showcase-score">
                      {t('certificates.detail.score')}: <strong>{item.score}</strong>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="btn-action btn-download" onClick={handleDownloadPDF}>
            📥 {t('certificates.detail.downloadPDF')}
          </button>
          <button className="btn-action btn-verify" onClick={copyVerifyLink}>
            🔗 {t('certificates.detail.copyLink')}
          </button>
          <button className="btn-action btn-qr" onClick={() => setShowQRModal(true)}>
            📱 {t('certificates.detail.showQR')}
          </button>
        </div>

        {/* Verification Link */}
        {certificate.public_token && (
          <div className="verification-section">
            <h3 className="verify-title">
              <span className="verify-icon">🔒</span>
              {t('certificates.detail.verifyTitle')}
            </h3>
            <p className="verify-subtitle">{t('certificates.detail.verifySubtitle')}</p>
            <div className="verify-link-box">
              <code>{`${window.location.origin}/verify/${certificate.public_token}`}</code>
              <button className="copy-btn" onClick={copyVerifyLink}>
                📋 {t('certificates.detail.copy')}
              </button>
            </div>
          </div>
        )}

        {/* Meta Information */}
        <div className="cert-meta">
          <div className="meta-item">
            <span className="meta-label">{t('certificates.detail.policyVersion')}:</span>
            <span className="meta-value">{certificate.policy_version || 'N/A'}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">{t('certificates.detail.scoringVersion')}:</span>
            <span className="meta-value">{certificate.scoring_version || 'N/A'}</span>
          </div>
          {certificate.template_version && (
            <div className="meta-item">
              <span className="meta-label">{t('certificates.detail.templateVersion')}:</span>
              <span className="meta-value">{certificate.template_version}</span>
            </div>
          )}
        </div>

      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="qr-modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="qr-close-btn" onClick={() => setShowQRModal(false)}>X</button>
            <h3>{t('certificates.detail.qrTitle')}</h3>
            <p className="qr-subtitle">{t('certificates.detail.qrSubtitle')}</p>
            {qrCodeUrl && <img src={qrCodeUrl} alt="QR Code" className="qr-code-image" />}
            <button className="btn btn-primary" onClick={() => setShowQRModal(false)}>
              {t('certificates.detail.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateDetailPage;

